import { useEffect, useRef } from "react";

import { useEventCallback } from "usehooks-ts";

import type { RefObject } from "react";

/**
 * Calls `callback` with `ref`'s element once it's attached and again after each mutation within it,
 * following the ref to a new element when it changes between renders.
 */
export function useMutationObserver<T extends HTMLElement>(
  ref: RefObject<T | null>,
  callback: (element: T) => void,
  options: MutationObserverInit,
) {
  const onChange = useEventCallback(callback);
  const observedRef = useRef<{ element: T; observer: MutationObserver } | null>(null);

  // No deps: a ref change doesn't re-render, so check on every render
  useEffect(() => {
    const element = ref.current;
    if (observedRef.current?.element === element) return;
    observedRef.current?.observer.disconnect();
    observedRef.current = null;
    if (!element) return;

    onChange(element);
    const observer = new MutationObserver(() => onChange(element));
    observer.observe(element, options);
    observedRef.current = { element, observer };
  });

  useEffect(
    () => () => {
      observedRef.current?.observer.disconnect();
      observedRef.current = null;
    },
    [],
  );
}
